import React, { memo, useState } from "react";
import { Paper, makeStyles } from "@material-ui/core";
import SelectButton from "@common/buttons/SelectButton";
import { store } from "@store";
import { TICKETS_PRIORITY } from "@tickets/utils";
import { useLocale } from "@hooks";
import {
  TicketItemDataFragment,
  TodoStatus,
  useAssignTodoMutation,
  useGetUserSelectUsersQuery,
  useSetTodoStatusMutation,
  UserSelectFragment
} from "@graphql";
import { TodoTags } from "@bonliva-crm/common";

type Props = {
  ticket: TicketItemDataFragment;
};

/**
 * CRMDEV-7103 follow-up: the remaining tickets are re-enabled for the two
 * actions users still need — reassign (managed by) and status (close). Tag and
 * priority stay readonly. Mutations return the updated TodoItem, so Apollo
 * normalises the change back into the profile cache by `_id`.
 */
function TicketsSideMenu({ ticket }: Props): React.ReactElement<Props> {
  const [users, setUsers] = useState<UserSelectFragment[]>([]);
  const { locale } = useLocale();
  const styles = useStyles({});

  useGetUserSelectUsersQuery({
    onCompleted(data) {
      setUsers(data.users);
    }
  });

  const [assignTodo] = useAssignTodoMutation();
  const [setTodoStatus] = useSetTodoStatusMutation();

  const noop = () => undefined;

  const handleManagedBySelect = async (value: string) => {
    // User-only reassignment (CRMDEV-7103 follow-up); team handoff is unchanged.
    if (!value || !value.endsWith("_user")) {
      return;
    }

    const userId = value.replace(/_user$/, "");

    try {
      await assignTodo({ variables: { todoId: ticket._id, userId } });
    } catch (e) {
      store().snackbars.error(`${locale.somethingWentWrong}\n${e}`);
    }
  };

  const handleStatusSelect = async (value: string) => {
    if (!value || value === ticket.Status) {
      return;
    }

    try {
      await setTodoStatus({
        variables: { todoId: ticket._id, status: value as TodoStatus }
      });
    } catch (e) {
      store().snackbars.error(`${locale.somethingWentWrong}\n${e}`);
    }
  };

  const renderManagedByDropdown = () => {
    const options = users.map((user) => ({
      label: user.Name,
      value: `${user._id}_user`
    }));

    let initialManagedByOption = null;

    if (ticket.AssignedTo && ticket.Accepted) {
      const foundUser = users.find((user) => user._id === ticket.AssignedTo.PersonId);

      if (foundUser) {
        initialManagedByOption = `${foundUser._id}_user`;
      }
    }

    return (
      <SelectButton
        multi={false}
        onSelect={handleManagedBySelect}
        options={options}
        initialOption={initialManagedByOption ? initialManagedByOption : ""}
        label={locale.ticketsProfile.dropdown.managedBy}
      />
    );
  };

  const renderStatusDropdown = () => {
    const options = Object.keys(TodoStatus).toLabelValue((key) => locale.todoStatus[key]);

    return (
      <SelectButton
        multi={false}
        onSelect={handleStatusSelect}
        options={options}
        initialOption={ticket.Status}
        label={locale.ticketsProfile.dropdown.status}
      />
    );
  };

  const renderTagDropdown = () => {
    const options = Object.values(TodoTags).toLabelValue();

    return (
      <SelectButton
        multi={true}
        onSelect={noop}
        options={options}
        initialOption={ticket.Tags as any}
        label={locale.ticketsProfile.dropdown.tags}
        disabled
      />
    );
  };

  const renderPriorityDropdown = () => {
    const options = Object.entries(TICKETS_PRIORITY).map(([key, value]) => ({
      label: value,
      value: key
    }));

    return (
      <SelectButton
        multi={false}
        onSelect={noop}
        options={options}
        initialOption={ticket.Priority}
        label={locale.ticketsProfile.dropdown.priority}
        disabled
      />
    );
  };

  return (
    <Paper style={{ padding: 16 }}>
      <div className={styles.dropdownWrapper}>{renderManagedByDropdown()}</div>
      <div className={styles.dropdownWrapper}>{renderStatusDropdown()}</div>
      <div className={styles.dropdownWrapper}>{renderTagDropdown()}</div>
      <div className={styles.dropdownWrapper}>{renderPriorityDropdown()}</div>
    </Paper>
  );
}

const useStyles = makeStyles({
  dropdownWrapper: {
    marginBottom: 24
  }
});

export default memo(TicketsSideMenu);
